import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, X, Calendar, Tag, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { fetchProjects } from "../lib/api";

/* ─── Project Detail Modal ─────────────────────────────────────── */
const ProjectModal = ({ project, onClose, onPrev, onNext, hasPrev, hasNext, lang, t }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext) onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  const title = (project.title && project.title[lang]) || project.title?.en || "";
  const description =
    (project.description && project.description[lang]) ||
    project.description?.en ||
    project.description ||
    "";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm px-4 py-6"
      onClick={onClose}
      data-testid="project-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.97 }}
        transition={{ duration: 0.45, ease: [0.65, 0, 0.35, 1] }}
        className="w-full max-w-5xl bg-mayak-surface border border-mayak-border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col lg:flex-row"
        onClick={(e) => e.stopPropagation()}
        data-testid="project-modal"
      >
        {/* Image */}
        <div className="relative w-full lg:w-[55%] aspect-[4/3] lg:aspect-auto shrink-0 overflow-hidden bg-mayak-bg">
          <img
            src={project.image_url}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          {/* Category badge */}
          <div className="absolute top-5 left-5 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-white/15 text-[10px] tracking-[0.25em] uppercase text-mayak-accent-bright">
            {project.category}
          </div>

          {/* Nav arrows over image */}
          <div className="absolute bottom-5 right-5 flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              disabled={!hasPrev}
              className="w-9 h-9 border border-white/30 flex items-center justify-center text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Previous project"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              disabled={!hasNext}
              className="w-9 h-9 border border-white/30 flex items-center justify-center text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Next project"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 overflow-y-auto p-8 lg:p-10">
          {/* Close */}
          <div className="flex justify-end mb-6">
            <button
              onClick={onClose}
              className="w-9 h-9 border border-mayak-border-strong flex items-center justify-center text-mayak-dim hover:border-mayak-accent hover:text-mayak-accent transition-all"
              data-testid="project-modal-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-[10px] tracking-[0.3em] uppercase text-mayak-accent mb-5">
            <span className="flex items-center gap-1.5">
              <Calendar size={11} />
              {project.year}
            </span>
            <span className="w-2 h-px bg-mayak-accent" />
            <span className="flex items-center gap-1.5">
              <MapPin size={11} />
              {project.location}
            </span>
            {project.category && (
              <>
                <span className="w-2 h-px bg-mayak-accent" />
                <span className="flex items-center gap-1.5">
                  <Tag size={11} />
                  {project.category}
                </span>
              </>
            )}
          </div>

          {/* Title */}
          <h2 className="font-black text-3xl lg:text-4xl text-mayak-text tracking-tight leading-[1.05] mb-6">
            {title}
          </h2>

          {/* Divider */}
          <div className="w-12 h-px bg-mayak-accent mb-6" />

          {/* Description */}
          {description ? (
            <p className="text-mayak-dim leading-relaxed text-sm lg:text-base mb-8">
              {description}
            </p>
          ) : (
            <p className="text-mayak-faint text-sm italic mb-8">
              {t("projects.noDescription") || "—"}
            </p>
          )}

          {/* Footer */}
          <div className="mt-auto pt-6 border-t border-mayak-border flex items-center justify-between">
            <span className="font-mono text-[10px] text-mayak-faint tracking-[0.3em] uppercase">
              Mayak Construction
            </span>
            {project.featured && (
              <span className="px-2.5 py-1 border border-mayak-accent text-[10px] tracking-[0.2em] uppercase text-mayak-accent">
                {t("projects.featured") || "Featured"}
              </span>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ─── Projects Section ──────────────────────────────────────────── */
const Projects = () => {
  const { t, lang } = useI18n();
  const [filter, setFilter] = useState("all");
  const [projects, setProjects] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    fetchProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return projects;
    return projects.filter((p) => p.category === filter);
  }, [filter, projects]);

  const openModal = useCallback((index) => setSelectedIndex(index), []);
  const closeModal = useCallback(() => setSelectedIndex(null), []);
  const prevProject = useCallback(() => setSelectedIndex((i) => (i > 0 ? i - 1 : i)), []);
  const nextProject = useCallback(
    () => setSelectedIndex((i) => (i < filtered.length - 1 ? i + 1 : i)),
    [filtered.length]
  );

  const filters = [
    { key: "all", label: t("projects.filters.all") },
    { key: "construction", label: t("projects.filters.construction") },
    { key: "renovation", label: t("projects.filters.renovation") },
    { key: "architecture", label: t("projects.filters.architecture") },
    { key: "consulting", label: t("projects.filters.consulting") }
  ];

  return (
    <>
      <section
        id="projects"
        className="bg-mayak-bg-alt text-mayak-text py-24 lg:py-36 relative"
        data-testid="projects-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
            <div className="lg:col-span-7">
              <span className="mayak-eyebrow">{t("projects.eyebrow")}</span>
              <h2 className="mt-6 font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.02] text-mayak-text">
                {t("projects.title")}
              </h2>
              <p className="mt-6 text-mayak-dim text-base lg:text-lg max-w-xl leading-relaxed">
                {t("projects.subtitle")}
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="flex flex-wrap gap-2 lg:justify-end" data-testid="project-filters">
                {filters.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`filter-pill ${filter === f.key ? "active" : ""}`}
                    data-testid={`filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <motion.article
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30 }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: [0.65, 0, 0.35, 1] }}
                  className={`project-card group cursor-pointer ${
                    p.featured && i === 0 ? "lg:col-span-2 lg:row-span-2" : ""
                  }`}
                  onClick={() => openModal(i)}
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="relative overflow-hidden bg-mayak-surface">
                    <div
                      className={`relative ${
                        p.featured && i === 0 ? "aspect-[16/12]" : "aspect-[4/5]"
                      }`}
                    >
                      <img
                        src={p.image_url}
                        alt={(p.title && p.title[lang]) || p.title?.en}
                        className="absolute inset-0 w-full h-full object-cover project-card-image"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent opacity-95 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-white/70 font-mono">
                        0{i + 1}
                      </div>
                      <div className="absolute top-5 right-5 px-2.5 py-1 bg-black/60 backdrop-blur-sm border border-white/15 text-[10px] tracking-[0.2em] uppercase text-mayak-accent-bright">
                        {p.category}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-7">
                      <div className="flex items-end justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-mayak-accent-bright mb-3">
                            <span>{p.year}</span>
                            <span className="w-2 h-px bg-mayak-accent-bright" />
                            <MapPin size={11} />
                            <span>{p.location}</span>
                          </div>
                          <h3 className="font-black text-xl lg:text-2xl text-white tracking-tight max-w-md">
                            {(p.title && p.title[lang]) || p.title?.en}
                          </h3>
                        </div>
                        <div className="shrink-0 w-11 h-11 border border-white/30 flex items-center justify-center text-white group-hover:bg-mayak-accent group-hover:border-mayak-accent group-hover:text-mayak-bg transition-all duration-500">
                          <ArrowUpRight size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </div>

          {filtered.length === 0 && (
            <div className="py-20 text-center text-mayak-faint" data-testid="no-projects">
              —
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedIndex !== null && filtered[selectedIndex] && (
          <ProjectModal
            project={filtered[selectedIndex]}
            onClose={closeModal}
            onPrev={prevProject}
            onNext={nextProject}
            hasPrev={selectedIndex > 0}
            hasNext={selectedIndex < filtered.length - 1}
            lang={lang}
            t={t}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Projects;
